import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Receipt, Search, ScanLine, PackageOpen, Edit2, Eye, Layers, Box, Package, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import api from '../lib/api';
import { playSuccessSound, playErrorSound, getPriceWithoutTVA, getTVAAmount, formatPrice, roundPrice, calcLineTotal } from '../lib/utils';

// Import custom hooks
import { useOrders } from '../hooks/useOrders';
import { useScanner } from '../hooks/useScanner';

// Import components
import { 
    CustomerCard, 
    NewProductModal, 
    CustomerSelectModal, 
    ViewOrderModal, 
    EditItemModal, 
    EditCustomerModal,
    MoveItemModal
} from '../components/orders';
import { VerificationModalV12 } from '../components/orders/VerificationModalV12';

// Check if V3 (TVA mode) is active
const isV3 = () => localStorage.getItem('app_version') === 'v3';

export const ComenziV2 = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    
    // Use custom hooks
    const {
        loading,
        customers,
        summary,
        activePallet,
        categories,
        loadData,
        toggleInformed,
        updateCustomer,
        deleteCustomer,
        getCustomerItems,
        updateItem,
        deleteItem,
        searchProduct,
        addFromStock,
        registerAndSell,
        completeVerified
    } = useOrders();

    // VERIFICARE - state local (NU mai folosim hook extern)
    const [showVerification, setShowVerification] = useState(false);
    const [verificationCustomer, setVerificationCustomer] = useState(null);
    const [verificationItems, setVerificationItems] = useState([]);
    
    // Scanner state
    const [showScanner, setShowScanner] = useState(false);
    const [scanMode, setScanMode] = useState('magazin');
    const [scannedProduct, setScannedProduct] = useState(null);
    const priceInputRef = useRef(null);
    
    // Sort state
    const [sortOrder, setSortOrder] = useState('alpha'); // 'alpha', 'newest', 'oldest', 'value'
    
    // Customers modal state
    const [showCustomersModal, setShowCustomersModal] = useState(false);
    const [customersModalFilter, setCustomersModalFilter] = useState('all'); // 'all', 'active'
    const [customersSearchQuery, setCustomersSearchQuery] = useState(''); // Search by number or name
    
    // New product modal (pallet scan)
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [newProductData, setNewProductData] = useState({
        barcode: '',
        name: '',
        category: '',
        quantity: '1'
    });
    
    // Customer selection modal
    const [showCustomerSelect, setShowCustomerSelect] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [targetCustomer, setTargetCustomer] = useState(null); // Pre-selected customer for quick add
    const [newCustomerMode, setNewCustomerMode] = useState(false);
    const [sellPrice, setSellPrice] = useState('');
    const [sellQty, setSellQty] = useState('1');
    const [newCustomerData, setNewCustomerData] = useState({
        customer_id: '',
        name: '',
        phone: '',
        address: ''
    });
    
    // View order modal
    const [showViewOrder, setShowViewOrder] = useState(false);
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [customerItems, setCustomerItems] = useState([]);
    
    // Edit item modal
    const [showEditItem, setShowEditItem] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editQty, setEditQty] = useState('1');
    const [editPrice, setEditPrice] = useState('');
    
    // Move item modal
    const [showMoveItem, setShowMoveItem] = useState(false);
    const [movingItem, setMovingItem] = useState(null);
    const [isMoving, setIsMoving] = useState(false); // Prevent double-submit
    
    // Edit customer modal
    const [showEditCustomer, setShowEditCustomer] = useState(false);
    const [editCustomerData, setEditCustomerData] = useState({
        customer_id: '',
        name: '',
        phone: '',
        email: '',
        address: ''
    });
    
    // Phone required modal - shown when customer doesn't have phone
    const [showPhoneRequiredModal, setShowPhoneRequiredModal] = useState(false);
    const [phoneRequiredCustomerId, setPhoneRequiredCustomerId] = useState('');
    const [phoneRequiredInput, setPhoneRequiredInput] = useState('');

    // Checked items state - synchronized via API (shared across devices)
    // Structure: { itemId: checkedCount } (number of items checked)
    const [checkedItems, setCheckedItems] = useState({});
    const [checkedItemsLoaded, setCheckedItemsLoaded] = useState(false);
    const lastLocalChangeRef = useRef(0); // Track when we last made a local change
    
    // Load checked items from API on mount
    useEffect(() => {
        const loadCheckedItems = async () => {
            try {
                const res = await api.get('/checked-items');
                setCheckedItems(res.data.checked || {});
            } catch (err) {
                console.error('Failed to load checked items:', err);
                // Fallback to localStorage
                try {
                    const saved = localStorage.getItem('orderCheckedItems');
                    if (saved) setCheckedItems(JSON.parse(saved));
                } catch {}
            }
            setCheckedItemsLoaded(true);
        };
        loadCheckedItems();
    }, []);
    
    // Save checked items to API (INSTANT - fără debounce pentru scanări rapide)
    const saveCheckedItems = useCallback(async (newState) => {
        // Mark that we made a local change
        lastLocalChangeRef.current = Date.now();
        
        // Salvare IMEDIATĂ - fără debounce
        try {
            await api.post('/checked-items', { checked: newState });
        } catch (err) {
            console.error('Failed to save checked items:', err);
        }
    }, []);

    // Box assignment modal state (when moving from orders to reservations)
    const [showBoxAssignModal, setShowBoxAssignModal] = useState(false);
    const [boxAssignCustomer, setBoxAssignCustomer] = useState(null);
    const [boxNumber, setBoxNumber] = useState('');
    const [freeBoxes, setFreeBoxes] = useState([]);
    
    // Location box for unconfirmed orders (L-type boxes)
    const [showLocationBoxModal, setShowLocationBoxModal] = useState(false);
    const [locationBoxCustomer, setLocationBoxCustomer] = useState(null);
    const [locationBox, setLocationBox] = useState('');

    // Toggle check for an item (manual click - toggles full check/uncheck)
    const handleToggleCheck = useCallback((itemId, itemQuantity = 1) => {
        setCheckedItems(prev => {
            const currentCount = prev[itemId] || 0;
            // If fully checked, uncheck completely. Otherwise, check fully.
            const newCount = currentCount >= itemQuantity ? 0 : itemQuantity;
            const newState = { ...prev, [itemId]: newCount };
            if (newCount === 0) delete newState[itemId];
            saveCheckedItems(newState);
            return newState;
        });
    }, [saveCheckedItems]);

    // Increment check count for an item (scan - adds 1 each time, or set full if setFull=true)
    const handleScanCheck = useCallback((itemId, itemQuantity = 1, setFull = false) => {
        setCheckedItems(prev => {
            const currentCount = prev[itemId] || 0;
            // If setFull, set to full quantity; otherwise increment by 1
            const newCount = setFull ? itemQuantity : Math.min(currentCount + 1, itemQuantity);
            const newState = { ...prev, [itemId]: newCount };
            saveCheckedItems(newState);
            return newState;
        });
    }, [saveCheckedItems]);

    // Initialize
    useEffect(() => {
        
        const handleScan = () => setShowScanner(true);
        window.addEventListener('v2-scan-comenzi', handleScan);
        return () => window.removeEventListener('v2-scan-comenzi', handleScan);
    }, []);
    
    // Periodic sync of checked items (every 10 seconds) for multi-device sync
    // SUSPENDAT când ORICE modal de verificare/vizualizare e deschis!
    useEffect(() => {
        if (!checkedItemsLoaded) return;
        
        const syncInterval = setInterval(async () => {
            // NU sincroniza când ORICE modal e deschis (ViewOrder SAU Verification)!
            if (showVerification || showViewOrder) {
                return; // Skip complet - nu suprascrie bifele
            }
            
            // Don't sync if we made a local change in the last 5 seconds
            // This prevents overwriting user's recent actions
            const timeSinceLastChange = Date.now() - lastLocalChangeRef.current;
            if (timeSinceLastChange < 5000) {
                return; // Skip this sync cycle
            }
            
            try {
                const res = await api.get('/checked-items');
                const serverState = res.data.checked || {};
                // Only update if different (avoid unnecessary re-renders)
                setCheckedItems(prev => {
                    const prevStr = JSON.stringify(prev);
                    const serverStr = JSON.stringify(serverState);
                    if (prevStr !== serverStr) {
                        return serverState;
                    }
                    return prev;
                });
            } catch (err) {
                // Silently ignore sync errors
            }
        }, 10000); // Every 10 seconds
        
        return () => clearInterval(syncInterval);
    }, [checkedItemsLoaded, showVerification, showViewOrder]);
    
    // Check for ?scan=true URL param
    useEffect(() => {
        if (searchParams.get('scan') === 'true') {
            setShowScanner(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Auto-focus price input
    useEffect(() => {
        if (scannedProduct && priceInputRef.current) {
            const timer = setTimeout(() => {
                priceInputRef.current?.focus();
                priceInputRef.current?.select();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [scannedProduct]);

    // Handle barcode scan
    const handleScannedBarcode = useCallback(async (barcode) => {
        setShowScanner(false);
        
        if (scanMode === 'palet') {
            if (!activePallet) {
                playErrorSound();
                toast.error('Nu există palet deschis! Deschide un palet în Recepție.');
                return;
            }
            
            try {
                // Check if product exists in database (products_v2)
                const productsRes = await api.get('/products/v2');
                const products = productsRes.data || [];
                const normalizedBarcode = barcode.toUpperCase();
                const dbProduct = products.find(p => p.barcode?.toUpperCase() === normalizedBarcode);
                
                if (dbProduct) {
                    // Product exists in DB - check store stock and proceed to customer selection
                    let storeStock = 0;
                    try {
                        const storeRes = await api.get('/store/v2');
                        const storeItem = (storeRes.data || []).find(s => s.barcode?.toUpperCase() === normalizedBarcode);
                        storeStock = storeItem?.quantity || 0;
                    } catch (e) {
                        console.error('Error fetching store stock:', e);
                    }
                    
                    setScannedProduct({
                        barcode: dbProduct.barcode,
                        name: dbProduct.name,
                        category: dbProduct.category,
                        source: 'palet_direct',
                        from_db: true,
                        storeStock: storeStock
                    });
                    setSellPrice('');
                    setSellQty('1');
                    // If we have a target customer, keep it selected
                    if (!targetCustomer) {
                        setSelectedCustomerId('');
                    }
                    setNewCustomerMode(false);
                    setShowCustomerSelect(true);
                } else {
                    // Product NOT in database - show form to create it
                    setNewProductData({
                        barcode: barcode,
                        name: '',
                        category: categories.length > 0 ? categories[0] : '',
                        quantity: '1'
                    });
                    setShowNewProduct(true);
                }
            } catch (err) {
                toast.error('Eroare la verificarea produsului');
                console.error(err);
            }
        } else {
            try {
                const res = await searchProduct(barcode);
                if (res.found) {
                    setScannedProduct({ ...res.product, source: res.source });
                    setSellPrice('');
                    setSellQty('1');
                    // If we have a target customer, keep it selected
                    if (!targetCustomer) {
                        setSelectedCustomerId('');
                    }
                    setNewCustomerMode(false);
                    setShowCustomerSelect(true);
                } else {
                    toast.error('Produs negăsit. Folosește "Scanează din Palet" pentru a-l înregistra.');
                }
            } catch (err) {
                console.error(err);
            }
        }
    }, [scanMode, activePallet, categories, searchProduct, targetCustomer]);
    
    // Handle new product from pallet
    const handleAddNewProductToPallet = useCallback(async () => {
        if (!newProductData.name.trim()) {
            toast.error('Introdu numele produsului');
            return;
        }
        if (!newProductData.category) {
            toast.error('Selectează o categorie');
            return;
        }
        
        // Check store stock for this barcode
        let storeStock = 0;
        try {
            const storeRes = await api.get('/store/v2');
            const normalizedBarcode = newProductData.barcode.toUpperCase();
            const storeItem = (storeRes.data || []).find(s => s.barcode?.toUpperCase() === normalizedBarcode);
            storeStock = storeItem?.quantity || 0;
        } catch (e) {
            console.error('Error fetching store stock:', e);
        }
        
        setScannedProduct({
            barcode: newProductData.barcode,
            name: newProductData.name,
            category: newProductData.category,
            source: 'palet_nou',
            isNewFromPallet: true,
            storeStock: storeStock
        });
        setSellPrice('');
        setSellQty('1');
        setSelectedCustomerId('');
        setNewCustomerMode(false);
        setShowNewProduct(false);
        setShowCustomerSelect(true);
    }, [newProductData]);

    // Add product to customer order
    const handleAddToOrder = useCallback(async () => {
        const customerId = newCustomerMode ? newCustomerData.customer_id : selectedCustomerId;
        
        if (!customerId) {
            toast.error('Selectează sau introdu un client');
            return;
        }
        if (!scannedProduct) return;
        
        const qty = parseInt(sellQty) || 1;
        const price = parseFloat(sellPrice) || 0;
        
        // Prepare customer data if new customer
        const customerData = newCustomerMode ? {
            customer_name: newCustomerData.name || '',
            customer_phone: newCustomerData.phone || '',
            customer_address: newCustomerData.address || ''
        } : {};
        
        // Check if price is 0 - ask for gift confirmation
        // NOTE: Negative prices are allowed (for advances/avansuri)
        let isGift = false;
        if (price === 0) {
            const confirmGift = window.confirm('Prețul este 0. Adaugi ca și CADOU?\n\n(Nu se scade din stoc, nu apare în facturi)');
            if (!confirmGift) {
                toast.info('Introdu un preț sau confirmă cadou');
                return;
            }
            isGift = true;
        }
        
        try {
            // Handle GIFT - add to order but don't deduct stock, don't include in reports
            if (isGift) {
                await api.post(`/orders/v2/customers/${customerId}/add-gift`, {
                    barcode: scannedProduct.barcode,
                    name: scannedProduct.name || scannedProduct.barcode,
                    category: scannedProduct.category || '',
                    quantity: qty,
                    ...customerData
                });
                playSuccessSound();
                toast.success(`🎁 CADOU: ${scannedProduct.name} (${qty} buc) pentru ${customerId}`, { duration: 4000 });
                
                // Close and reset
                setShowCustomerSelect(false);
                setScannedProduct(null);
                loadData();
                setTimeout(() => setShowScanner(true), 300);
                return;
            }
            
            if (scannedProduct.isNewFromPallet) {
                // New product - register and sell (creates product + pallet item + order)
                // Cantitatea de pe palet = cantitatea dată la client
                const res = await registerAndSell({
                    barcode: scannedProduct.barcode,
                    name: scannedProduct.name,
                    category: scannedProduct.category,
                    quantity_on_pallet: qty,  // Same as quantity to sell
                    quantity_to_sell: qty,
                    unit_price: price,
                    customer_id: customerId,
                    ...customerData
                });
                playSuccessSound();
                
                // Check if taken from store
                if (res?.took_from_store) {
                    toast.success(`${scannedProduct.name} luat din MAGAZIN (${res.store_qty_taken} buc)`);
                } else {
                    toast.success(`${scannedProduct.name} (${qty} buc) adăugat la comanda ${customerId}`);
                }
            } else if (scannedProduct.from_db || scannedProduct.source === 'palet_direct') {
                // Product exists in DB - add from pallet flow
                const res = await api.post(`/orders/v2/customers/${customerId}/add-from-pallet`, {
                    pallet_id: activePallet.id,
                    barcode: scannedProduct.barcode,
                    name: scannedProduct.name || scannedProduct.barcode,
                    category: scannedProduct.category || '',
                    quantity: qty,
                    unit_price: price,
                    ...customerData
                });
                playSuccessSound();
                
                // Check if taken from store
                if (res.data?.took_from_store) {
                    toast.success(`${scannedProduct.name} luat din MAGAZIN (${res.data.store_qty_taken} buc)`);
                } else {
                    toast.success(`${scannedProduct.name} (${qty} buc) adăugat la comanda ${customerId}`);
                }
                
                // Check if customer needs phone - show modal
                if (res.data?.customer_needs_phone) {
                    setPhoneRequiredCustomerId(customerId);
                    setPhoneRequiredInput('');
                    setShowPhoneRequiredModal(true);
                }
            } else {
                // Normal flow - from store/depot
                const res = await addFromStock(customerId, {
                    barcode: scannedProduct.barcode,
                    quantity: qty,
                    unit_price: price,
                    force_from_depot: false,
                    ...customerData
                });
                
                if (res.status === 'requires_confirmation') {
                    if (window.confirm(res.message)) {
                        const res2 = await addFromStock(customerId, {
                            barcode: scannedProduct.barcode,
                            quantity: qty,
                            unit_price: price,
                            force_from_depot: true,
                            ...customerData
                        });
                        playSuccessSound();
                        toast.success(`${scannedProduct.name} adăugat din Depozit`);
                        
                        // Check if customer needs phone
                        if (res2?.customer_needs_phone) {
                            setPhoneRequiredCustomerId(customerId);
                            setPhoneRequiredInput('');
                            setShowPhoneRequiredModal(true);
                        }
                    } else {
                        toast.info('Operațiune anulată');
                        return;
                    }
                } else {
                    playSuccessSound();
                    toast.success(`${scannedProduct.name} adăugat la comandă`);
                    
                    // Check if customer needs phone
                    if (res?.customer_needs_phone) {
                        setPhoneRequiredCustomerId(customerId);
                        setPhoneRequiredInput('');
                        setShowPhoneRequiredModal(true);
                    }
                }
            }
            
            // Close customer select modal and reset product
            setShowCustomerSelect(false);
            setScannedProduct(null);
            // Keep targetCustomer so scanner continues for same customer
            // setTargetCustomer(null);  -- removed to maintain target
            loadData();
            
            // Re-open scanner for continuous scanning
            setTimeout(() => {
                setShowScanner(true);
            }, 300);
        } catch (err) {
            playErrorSound();
            console.error(err);
        }
    }, [scannedProduct, selectedCustomerId, newCustomerMode, newCustomerData, sellQty, sellPrice, registerAndSell, addFromStock, loadData, scanMode, targetCustomer]);

    // Add transport fee to customer
    const handleAddTransportFee = useCallback(async (customer) => {
        try {
            await api.post(`/orders/v2/customers/${customer.customer_id}/add-transport-fee`);
            playSuccessSound();
            toast.success(`Taxa de transport adăugată pentru ${customer.customer_id}`);
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la adăugare taxă');
        }
    }, [loadData]);

    // View customer order
    const handleViewOrder = useCallback(async (customer) => {
        setViewingCustomer(customer);
        try {
            const items = await getCustomerItems(customer.customer_id);
            setCustomerItems(items);
            setShowViewOrder(true);
        } catch (err) {
            console.error(err);
        }
    }, [getCustomerItems]);

    // Edit item
    const handleEditItem = useCallback((item) => {
        setEditingItem(item);
        setEditQty(item.quantity.toString());
        setEditPrice(item.unit_price.toString());
        setShowEditItem(true);
    }, []);

    const handleSaveEdit = useCallback(async () => {
        if (!editingItem) return;
        try {
            await updateItem(editingItem.id, {
                quantity: parseInt(editQty) || 1,
                unit_price: parseFloat(editPrice) || 0
            });
            setShowEditItem(false);
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            console.error(err);
        }
    }, [editingItem, editQty, editPrice, updateItem, viewingCustomer, getCustomerItems]);

    // Edit customer
    const handleEditCustomer = useCallback((customer) => {
        setEditCustomerData({
            customer_id: customer.customer_id,
            name: customer.name || '',
            phone: customer.phone || '',
            email: customer.email || '',
            address: customer.address || ''
        });
        setShowEditCustomer(true);
    }, []);

    const handleSaveCustomer = useCallback(async () => {
        if (!editCustomerData.customer_id) return;
        try {
            await updateCustomer(editCustomerData.customer_id, editCustomerData);
            setShowEditCustomer(false);
            if (viewingCustomer) {
                setViewingCustomer({
                    ...viewingCustomer,
                    name: editCustomerData.name,
                    phone: editCustomerData.phone,
                    email: editCustomerData.email,
                    address: editCustomerData.address
                });
            }
            loadData(); // Refresh to update card colors
        } catch (err) {
            console.error(err);
        }
    }, [editCustomerData, updateCustomer, viewingCustomer, loadData]);
    
    // Save phone number for customer (from phone required modal)
    const handleSavePhoneRequired = useCallback(async () => {
        if (!phoneRequiredCustomerId || !phoneRequiredInput.trim()) {
            toast.error('Introdu numărul de telefon');
            return;
        }
        try {
            await updateCustomer(phoneRequiredCustomerId, { phone: phoneRequiredInput.trim() });
            toast.success('Telefon salvat');
            setShowPhoneRequiredModal(false);
            setPhoneRequiredInput('');
            setPhoneRequiredCustomerId('');
            loadData(); // Refresh to update card colors
        } catch (err) {
            toast.error('Eroare la salvare telefon');
            console.error(err);
        }
    }, [phoneRequiredCustomerId, phoneRequiredInput, updateCustomer, loadData]);

    // Delete item
    const handleDeleteItem = useCallback(async (item) => {
        const isFee = item.is_fee || item.source === 'taxa';
        const confirmMsg = isFee 
            ? `Anulezi "${item.name}"? (Clientul ridică personal)` 
            : `Ștergi "${item.name}" din comandă?`;
        
        if (!window.confirm(confirmMsg)) return;
        try {
            await deleteItem(item.id, true);
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            console.error(err);
        }
    }, [deleteItem, viewingCustomer, getCustomerItems]);

    // Delete customer
    const handleDeleteCustomer = useCallback(async (customer) => {
        if (!window.confirm(`Ștergi clientul ${customer.customer_id} și toate produsele din comandă?`)) return;
        try {
            await deleteCustomer(customer.customer_id);
        } catch (err) {
            console.error(err);
        }
    }, [deleteCustomer]);

    // Move item to another customer
    const handleMoveItem = useCallback((item) => {
        setMovingItem(item);
        setShowMoveItem(true);
    }, []);

    // Confirm move item
    const handleMoveConfirm = useCallback(async (item, targetCustomerId) => {
        // Prevent double-submit
        if (isMoving) {
            console.log('[MOVE] Already moving, ignoring');
            return;
        }
        
        setIsMoving(true);
        try {
            // Use dedicated move endpoint - doesn't affect stock
            await api.post(`/orders/v2/items/${item.id}/move-to-customer/${targetCustomerId}`);
            
            playSuccessSound();
            toast.success(`"${item.name}" mutat la ${targetCustomerId}`);
            
            setShowMoveItem(false);
            setMovingItem(null);
            
            // Refresh data
            loadData();
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        } finally {
            setIsMoving(false);
        }
    }, [loadData, viewingCustomer, getCustomerItems, isMoving]);

    // Speak message using Web Speech API - MUST be defined before handlers that use it
    const speakMessage = useCallback((message) => {
        if ('speechSynthesis' in window) {
            // Cancel any ongoing speech first
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(message);
            utterance.lang = 'ro-RO';
            utterance.rate = 0.9;
            utterance.pitch = 1;
            utterance.volume = 1;
            window.speechSynthesis.speak(utterance);
        }
    }, []);

    // Move item to reservations
    const handleMoveToReservation = useCallback(async (item) => {
        // Prevent double-submit
        if (isMoving) {
            console.log('[MOVE] Already moving, ignoring');
            return;
        }
        
        setIsMoving(true);
        try {
            const res = await api.post(`/orders/v2/items/${item.id}/move-to-reservation`);
            
            playSuccessSound();
            
            // Check if we need to assign a box
            if (res.data.needs_box) {
                // Customer moved to reservations needs a box
                const freeRes = await api.get('/reservation-boxes/v2/free');
                setFreeBoxes(freeRes.data || []);
                setBoxAssignCustomer(res.data.customer_id);
                setBoxNumber('');
                setShowBoxAssignModal(true);
                toast.info(`"${item.name}" mutat - atribuie cutia!`);
            } else {
                // Show appropriate message
                const boxMessage = res.data.box_message;
                if (boxMessage) {
                    toast.success(boxMessage, { duration: 4000 });
                    // Speak the message
                    speakMessage(boxMessage);
                } else {
                    toast.success(`"${item.name}" mutat la rezervări`);
                }
            }
            
            setShowMoveItem(false);
            setMovingItem(null);
            
            // Refresh data
            loadData();
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        } finally {
            setIsMoving(false);
        }
    }, [loadData, viewingCustomer, getCustomerItems, speakMessage, isMoving]);

    // Assign box from orders page
    const handleAssignBoxFromOrders = async () => {
        if (!boxAssignCustomer || !boxNumber) {
            toast.error('Introdu numărul cutiei');
            return;
        }
        
        try {
            const boxValue = boxNumber.toUpperCase().trim();
            const res = await api.post('/reservation-boxes/v2/assign', {
                customer_id: boxAssignCustomer,
                box_number: boxValue
            });
            
            playSuccessSound();
            const message = res.data.box_message || `CUTIA ${boxValue}`;
            toast.success(message, { duration: 4000 });
            speakMessage(message);
            
            setShowBoxAssignModal(false);
            setBoxAssignCustomer(null);
            setBoxNumber('');
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la atribuire cutie');
        }
    };

    // Assign location box (L-type) to unconfirmed order
    const handleAssignLocationBox = async () => {
        if (!locationBoxCustomer || !locationBox) {
            toast.error('Introdu numărul cutiei de localizare');
            return;
        }
        
        try {
            const boxValue = locationBox.toUpperCase().trim();
            await api.post('/order-boxes/assign', {
                customer_id: locationBoxCustomer,
                box_number: boxValue
            });
            
            playSuccessSound();
            const message = `Comandă localizată în ${boxValue}`;
            toast.success(message);
            speakMessage(boxValue);
            
            setShowLocationBoxModal(false);
            setLocationBoxCustomer(null);
            setLocationBox('');
            loadData(); // Refresh to show the new location
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la localizare');
        }
    };
    
    // Remove location box
    const handleRemoveLocationBox = async (customerId) => {
        try {
            await api.delete(`/order-boxes/release/${customerId}`);
            toast.success('Localizare eliminată');
            loadData();
        } catch (err) {
            toast.error('Eroare la eliminare');
        }
    };
    
    // Open location box modal
    const openLocationBoxModal = (customerId, currentBox = '') => {
        setLocationBoxCustomer(customerId);
        setLocationBox(currentBox || '');
        setShowLocationBoxModal(true);
    };

    // Bulk delete items - return to store
    const handleBulkDelete = useCallback(async (itemsList) => {
        if (!itemsList || itemsList.length === 0) return;
        
        const confirmMsg = `Ștergi ${itemsList.length} produse din comandă? Se vor întoarce în magazin.`;
        if (!window.confirm(confirmMsg)) return;
        
        try {
            let successCount = 0;
            for (const item of itemsList) {
                try {
                    await deleteItem(item.id, true);
                    successCount++;
                    // Clear checkbox for this item
                    if (handleToggleCheck) {
                        setCheckedItems(prev => {
                            const newState = { ...prev };
                            delete newState[item.id];
                            saveCheckedItems(newState);
                            return newState;
                        });
                    }
                } catch (e) {
                    console.error(`Failed to delete item ${item.id}:`, e);
                }
            }
            
            playSuccessSound();
            toast.success(`${successCount} produse șterse și returnate în magazin`);
            
            // Refresh data
            loadData();
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la ștergere');
        }
    }, [deleteItem, loadData, viewingCustomer, getCustomerItems]);

    // Bulk move items to another customer - opens modal for selection
    const [bulkMovingItems, setBulkMovingItems] = useState([]);
    const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
    
    const handleBulkMove = useCallback((itemsList) => {
        if (!itemsList || itemsList.length === 0) return;
        setBulkMovingItems(itemsList);
        setShowBulkMoveModal(true);
    }, []);

    const handleBulkMoveConfirm = useCallback(async (targetCustomerId) => {
        if (!bulkMovingItems.length || !targetCustomerId) return;
        
        try {
            let successCount = 0;
            for (const item of bulkMovingItems) {
                try {
                    await api.post(`/orders/v2/items/${item.id}/move-to-customer/${targetCustomerId}`);
                    successCount++;
                    // Clear checkbox
                    setCheckedItems(prev => {
                        const newState = { ...prev };
                        delete newState[item.id];
                        saveCheckedItems(newState);
                        return newState;
                    });
                } catch (e) {
                    console.error(`Failed to move item ${item.id}:`, e);
                }
            }
            
            playSuccessSound();
            toast.success(`${successCount} produse mutate la ${targetCustomerId}`);
            
            setShowBulkMoveModal(false);
            setBulkMovingItems([]);
            
            // Refresh data
            loadData();
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la mutare');
        }
    }, [bulkMovingItems, loadData, viewingCustomer, getCustomerItems]);

    // Bulk move items to reservations
    const handleBulkMoveToReservation = useCallback(async (itemsList) => {
        if (!itemsList || itemsList.length === 0) return;
        
        const confirmMsg = `Muți ${itemsList.length} produse la Rezervări?`;
        if (!window.confirm(confirmMsg)) return;
        
        try {
            let successCount = 0;
            let lastCustomerNeedsBox = null;
            
            for (const item of itemsList) {
                try {
                    const res = await api.post(`/orders/v2/items/${item.id}/move-to-reservation`);
                    successCount++;
                    
                    // Track if any customer needs a box
                    if (res.data.needs_box) {
                        lastCustomerNeedsBox = res.data.customer_id;
                    }
                    
                    // Clear checkbox
                    setCheckedItems(prev => {
                        const newState = { ...prev };
                        delete newState[item.id];
                        saveCheckedItems(newState);
                        return newState;
                    });
                } catch (e) {
                    console.error(`Failed to move item ${item.id}:`, e);
                }
            }
            
            playSuccessSound();
            toast.success(`${successCount} produse mutate la Rezervări`);
            
            // If any customer needs a box, show assignment modal
            if (lastCustomerNeedsBox) {
                const freeRes = await api.get('/reservation-boxes/v2/free');
                setFreeBoxes(freeRes.data || []);
                setBoxAssignCustomer(lastCustomerNeedsBox);
                setBoxNumber('');
                setShowBoxAssignModal(true);
            }
            
            // Refresh data
            loadData();
            if (viewingCustomer) {
                const items = await getCustomerItems(viewingCustomer.customer_id);
                setCustomerItems(items);
            }
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la mutare');
        }
    }, [loadData, viewingCustomer, getCustomerItems]);

    // Complete order - open verification
    const handleCompleteOrder = useCallback((customer) => {
        const items = customer.items || [];
        setVerificationCustomer(customer);
        setVerificationItems(items);
        setShowVerification(true);
        setShowViewOrder(false);
    }, []);
    
    // Finalize verified order - primește date de la modal
    const handleFinalizeVerified = useCallback(async (verificationData, stats) => {
        const fullyVerified = verificationData.filter(i => i.verifiedCount >= i.quantity);
        
        if (fullyVerified.length === 0) {
            toast.error('Trebuie să verifici complet cel puțin un produs!');
            return;
        }
        
        const totalVerifiedQty = fullyVerified.reduce((sum, i) => sum + i.quantity, 0);
        let confirmMsg = `Finalizezi ${fullyVerified.length} produse (${totalVerifiedQty} buc)`;
        if (stats.includeTransport) confirmMsg += ' + taxa transport';
        confirmMsg += '?';
        
        if (stats.unverifiedQty > 0) {
            confirmMsg += `\n\n${stats.unverifiedQty} bucăți neverificate se vor întoarce în magazin.`;
        }
        
        if (!window.confirm(confirmMsg)) return;
        
        try {
            await completeVerified(
                verificationCustomer.customer_id,
                verificationData,
                'return', // default action
                stats.includeTransport
            );
            
            playSuccessSound();
            if (stats.unverifiedQty > 0) {
                toast.success(`Comandă finalizată! ${stats.unverifiedQty} buc returnate în magazin.`);
            } else {
                toast.success('Comandă finalizată!');
            }
            
            setShowVerification(false);
            setVerificationCustomer(null);
            setVerificationItems([]);
        } catch (err) {
            playErrorSound();
            console.error(err);
        }
    }, [verificationCustomer, completeVerified]);

    // Copy order to clipboard
    const handleCopyOrder = useCallback((customer) => {
        const items = customer.items || [];
        let text = '';
        let lineNum = 1;
        let transportFee = 0;
        
        // Separate regular items, gifts, and fees
        const regularItems = items.filter(item => !item.is_fee && item.source !== 'taxa' && !item.is_gift && item.source !== 'gift');
        const giftItems = items.filter(item => item.is_gift || item.source === 'gift');
        const feeItems = items.filter(item => item.is_fee || item.source === 'taxa');
        
        // Regular items with prices
        regularItems.forEach(item => {
            const lineTotal = calcLineTotal(item.quantity, item.unit_price);
            text += `${lineNum}. ${item.name} x${item.quantity} = ${formatPrice(lineTotal)} RON\n`;
            lineNum++;
        });
        
        // Gift items - separated, without price
        if (giftItems.length > 0) {
            text += `\nCADOU\n`;
            giftItems.forEach(item => {
                if (item.quantity > 1) {
                    text += `${item.name} x${item.quantity}\n`;
                } else {
                    text += `${item.name}\n`;
                }
            });
        }
        
        // Transport fee
        feeItems.forEach(item => {
            transportFee += calcLineTotal(item.quantity, item.unit_price);
        });
        
        if (transportFee > 0) {
            text += `\nTransport: ${formatPrice(transportFee)} RON`;
        }
        
        // Total (excluding gifts since they're free) - with rounding
        const total = regularItems.reduce((sum, i) => sum + calcLineTotal(i.quantity, i.unit_price), 0) + transportFee;
        text += `\n\nTOTAL: ${formatPrice(roundPrice(total))} RON`;
        
        navigator.clipboard.writeText(text);
        toast.success('Comandă copiată!');
    }, []);

    // Scanner handlers
    const handleOpenScan = useCallback(() => {
        setScanMode('magazin');
        setShowScanner(true);
    }, []);

    const handleOpenScanFromPallet = useCallback(() => {
        if (!activePallet) {
            toast.error('Nu există palet deschis! Deschide un palet în Recepție.');
            return;
        }
        setScanMode('palet');
        setShowScanner(true);
    }, [activePallet]);

    // Filter customers
    const filteredCustomers = customers.filter(c => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            c.customer_id?.toLowerCase().includes(s) ||
            c.name?.toLowerCase().includes(s) ||
            c.phone?.toLowerCase().includes(s)
        );
    });

    // Sort function
    const sortCustomers = (customerList) => {
        return [...customerList].sort((a, b) => {
            switch (sortOrder) {
                case 'alpha':
                    return (a.customer_id || '').localeCompare(b.customer_id || '');
                case 'newest':
                    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                case 'oldest':
                    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                case 'value':
                    return (b.order_total || 0) - (a.order_total || 0);
                default:
                    return 0;
            }
        });
    };

    const activeCustomers = sortCustomers(filteredCustomers.filter(c => c.item_count > 0));

    // Consolidate duplicate items
    const handleConsolidateDuplicates = async () => {
        try {
            const res = await api.post('/orders/v2/merge-duplicates');
            if (res.data.merged_groups > 0) {
                toast.success(res.data.message);
                loadData(); // Refresh the data
            } else {
                toast.info('Nu există duplicate de combinat');
            }
        } catch (err) {
            toast.error('Eroare la consolidare');
            console.error(err);
        }
    };

    if (loading) {
        return (
            <div className="pb-24">
                <Skeleton className="h-10 w-48 mb-4" />
                <Skeleton className="h-24 w-full mb-4" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="comenzi-v2-page">
            {/* Fixed Header Section */}
            <div className="flex-shrink-0">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Comenzi
                        </h1>
                        <p className="text-zinc-500 mt-1">Gestionează comenzile clienților</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            onClick={handleOpenScan}
                            data-testid="scan-product-btn"
                            className="h-12 bg-violet-600 hover:bg-violet-700 text-white"
                        >
                            <ScanLine size={20} className="mr-2" />
                            Scanează Produs
                        </Button>
                        <Button
                            onClick={handleOpenScanFromPallet}
                            data-testid="scan-from-pallet-btn"
                            className="h-12 bg-orange-600 hover:bg-orange-700 text-white"
                        >
                            <PackageOpen size={20} className="mr-2" />
                            Scanează din Palet
                        </Button>
                        <Button
                            onClick={handleConsolidateDuplicates}
                            variant="outline"
                            className="h-12 border-teal-300 text-teal-700 hover:bg-teal-50"
                            title="Combină produsele duplicate din aceeași comandă"
                        >
                            <Layers size={20} className="mr-2" />
                            Consolidează
                        </Button>
                    </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 sm:p-4 text-center">
                        <div className="text-xl sm:text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {formatPrice(summary.total_value)}
                        </div>
                        <p className="text-xs text-violet-500">RON Total</p>
                        {isV3() && summary.total_value > 0 && (
                            <div className="mt-1 text-xs text-violet-700">
                                <span>Fără TVA: {formatPrice(getPriceWithoutTVA(summary.total_value))}</span>
                                <span className="mx-1">|</span>
                                <span className="text-blue-600 font-medium">TVA: {formatPrice(getTVAAmount(summary.total_value))}</span>
                            </div>
                        )}
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:p-4 text-center">
                        <div className="text-xl sm:text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {summary.total_items}
                        </div>
                        <p className="text-xs text-emerald-500">Produse</p>
                    </div>
                    <div 
                        className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-amber-100 transition-colors"
                        onClick={() => {
                            setCustomersModalFilter('active');
                            setShowCustomersModal(true);
                        }}
                    >
                        <div className="text-xl sm:text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {summary.active_customers}
                        </div>
                        <p className="text-xs text-amber-500">Clienți Activi</p>
                    </div>
                    <div 
                        className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-zinc-100 transition-colors"
                        onClick={() => {
                            setCustomersModalFilter('all');
                            setShowCustomersModal(true);
                        }}
                    >
                        <div className="text-xl sm:text-2xl font-bold text-zinc-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {summary.total_customers || 0}
                        </div>
                        <p className="text-xs text-zinc-500">Clienți Total</p>
                    </div>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Caută client după ID, nume sau telefon..."
                        className="pl-10 h-11"
                        data-testid="search-customer-input"
                    />
                </div>
            </div>

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto pb-24">
                {/* Active Orders */}
                {activeCustomers.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-zinc-700 flex items-center gap-2">
                                <Receipt size={18} className="text-violet-500" />
                                Comenzi Active ({activeCustomers.length})
                            </h2>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="text-sm border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-600"
                            >
                                <option value="alpha">A-Z</option>
                                <option value="newest">Cele mai noi</option>
                                <option value="oldest">Cele mai vechi</option>
                                <option value="value">După valoare</option>
                            </select>
                        </div>
                        <div className="space-y-3">
                            {activeCustomers.map((customer, index) => (
                                <CustomerCard
                                    key={customer.customer_id}
                                    customer={customer}
                                    index={index}
                                    onAdd={() => {
                                        setTargetCustomer(customer);
                                        setSelectedCustomerId(customer.customer_id);
                                        setScanMode('magazin');
                                        setShowScanner(true);
                                    }}
                                    onAddFromPalet={activePallet ? () => {
                                        setTargetCustomer(customer);
                                        setSelectedCustomerId(customer.customer_id);
                                        setScanMode('palet');
                                        setShowScanner(true);
                                    } : null}
                                    onAddTransportFee={() => handleAddTransportFee(customer)}
                                    onCopy={() => handleCopyOrder(customer)}
                                    onView={() => handleViewOrder(customer)}
                                    onComplete={() => handleCompleteOrder(customer)}
                                    onEdit={() => handleEditCustomer(customer)}
                                    onDelete={() => handleDeleteCustomer(customer)}
                                    onToggleInformed={() => toggleInformed(customer.customer_id)}
                                    onSetLocationBox={openLocationBoxModal}
                                    onRemoveLocationBox={handleRemoveLocationBox}
                                />
                            ))}
                        </div>
                    </div>
            )}

            {/* Empty state */}
            {activeCustomers.length === 0 && (
                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-8 text-center">
                    <Receipt size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Nicio comandă activă</h2>
                    <p className="text-zinc-500 mb-4">Scanează un produs pentru a începe o comandă</p>
                    <Button onClick={handleOpenScan} className="bg-violet-600 hover:bg-violet-700">
                        <ScanLine size={18} className="mr-2" />
                        Scanează Produs
                    </Button>
                </div>
            )}
            </div>
            {/* End of Scrollable Content */}

            {/* Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleScannedBarcode}
            />

            {/* Modals */}
            <NewProductModal
                open={showNewProduct}
                onOpenChange={setShowNewProduct}
                productData={newProductData}
                onProductDataChange={setNewProductData}
                categories={categories}
                onSubmit={handleAddNewProductToPallet}
            />

            <CustomerSelectModal
                open={showCustomerSelect}
                onOpenChange={(open) => {
                    setShowCustomerSelect(open);
                    if (!open) {
                        setScannedProduct(null);
                        setTargetCustomer(null);
                    }
                }}
                scannedProduct={scannedProduct}
                activeCustomers={activeCustomers}
                selectedCustomerId={selectedCustomerId}
                onSelectCustomer={setSelectedCustomerId}
                targetCustomer={targetCustomer}
                onClearTargetCustomer={() => {
                    setTargetCustomer(null);
                    setSelectedCustomerId('');
                }}
                newCustomerMode={newCustomerMode}
                onToggleNewCustomerMode={setNewCustomerMode}
                newCustomerData={newCustomerData}
                onNewCustomerDataChange={setNewCustomerData}
                sellPrice={sellPrice}
                onSellPriceChange={setSellPrice}
                sellQty={sellQty}
                onSellQtyChange={setSellQty}
                priceInputRef={priceInputRef}
                onSubmit={handleAddToOrder}
            />

            <ViewOrderModal
                open={showViewOrder}
                onOpenChange={setShowViewOrder}
                customer={viewingCustomer}
                items={customerItems}
                onEditCustomer={() => {
                    setEditCustomerData({
                        customer_id: viewingCustomer?.customer_id || '',
                        name: viewingCustomer?.name || '',
                        phone: viewingCustomer?.phone || '',
                        address: viewingCustomer?.address || ''
                    });
                    setShowEditCustomer(true);
                }}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
                onMoveItem={handleMoveItem}
                onCopy={() => handleCopyOrder(viewingCustomer)}
                onComplete={() => handleCompleteOrder(viewingCustomer)}
                checkedItems={checkedItems}
                onToggleCheck={handleToggleCheck}
                onScanCheck={handleScanCheck}
                onBulkDelete={handleBulkDelete}
                onBulkMove={handleBulkMove}
                onBulkMoveToReservation={handleBulkMoveToReservation}
            />

            {/* Bulk Move Modal */}
            <Dialog open={showBulkMoveModal} onOpenChange={setShowBulkMoveModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Mută {bulkMovingItems.length} produse la alt client
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700">
                                Selectează clientul destinație pentru {bulkMovingItems.length} produse
                            </p>
                        </div>
                        
                        {activeCustomers.filter(c => c.customer_id !== viewingCustomer?.customer_id).length > 0 && (
                            <div>
                                <p className="text-sm text-zinc-600 mb-2">Clienți existenți:</p>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    {activeCustomers
                                        .filter(c => c.customer_id !== viewingCustomer?.customer_id)
                                        .map(c => (
                                            <button
                                                key={c.customer_id}
                                                onClick={() => handleBulkMoveConfirm(c.customer_id)}
                                                className="px-3 py-2 rounded-lg text-sm font-medium bg-zinc-100 text-zinc-700 hover:bg-blue-100 hover:text-blue-700 transition-colors"
                                            >
                                                {c.customer_id}
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <p className="text-sm text-zinc-600 mb-2">Sau introdu ID client nou:</p>
                            <div className="flex gap-2">
                                <Input
                                    id="bulk-move-target"
                                    placeholder="Ex: CLIENT123"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.target.value) {
                                            handleBulkMoveConfirm(e.target.value);
                                        }
                                    }}
                                />
                                <Button
                                    onClick={() => {
                                        const input = document.getElementById('bulk-move-target');
                                        if (input?.value) handleBulkMoveConfirm(input.value);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    Mută
                                </Button>
                            </div>
                        </div>
                        
                        <Button
                            variant="outline"
                            onClick={() => setShowBulkMoveModal(false)}
                            className="w-full"
                        >
                            Anulează
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <MoveItemModal
                open={showMoveItem}
                onOpenChange={setShowMoveItem}
                item={movingItem}
                activeCustomers={activeCustomers}
                currentCustomerId={viewingCustomer?.customer_id}
                onMove={handleMoveConfirm}
                onMoveToReservation={handleMoveToReservation}
            />

            <EditItemModal
                open={showEditItem}
                onOpenChange={setShowEditItem}
                item={editingItem}
                editQty={editQty}
                onEditQtyChange={setEditQty}
                editPrice={editPrice}
                onEditPriceChange={setEditPrice}
                onSave={handleSaveEdit}
            />

            <EditCustomerModal
                open={showEditCustomer}
                onOpenChange={setShowEditCustomer}
                customerData={editCustomerData}
                onCustomerDataChange={setEditCustomerData}
                onSave={handleSaveCustomer}
            />

            {/* Phone Required Modal - shown when adding product to customer without phone */}
            <Dialog open={showPhoneRequiredModal} onOpenChange={setShowPhoneRequiredModal}>
                <DialogContent className="sm:max-w-md z-[200]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Phone size={20} />
                            Telefon necesar
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-zinc-600">
                            Clientul <strong>{phoneRequiredCustomerId}</strong> nu are număr de telefon. 
                            Introdu telefonul pentru a continua:
                        </p>
                        <Input
                            type="tel"
                            placeholder="07xxxxxxxx"
                            value={phoneRequiredInput}
                            onChange={(e) => setPhoneRequiredInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && phoneRequiredInput.trim()) {
                                    handleSavePhoneRequired();
                                }
                            }}
                            autoFocus
                            className="text-lg"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowPhoneRequiredModal(false);
                                setPhoneRequiredInput('');
                            }}
                        >
                            Mai târziu
                        </Button>
                        <Button
                            onClick={handleSavePhoneRequired}
                            disabled={!phoneRequiredInput.trim()}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            Salvează telefon
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Verification Modal V12 - COMPLET INDEPENDENT */}
            <VerificationModalV12
                open={showVerification}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowVerification(false);
                    }
                }}
                customer={verificationCustomer}
                initialItems={verificationItems}
                onFinalize={handleFinalizeVerified}
            />

            {/* Customers List Modal */}
            <Dialog open={showCustomersModal} onOpenChange={(open) => {
                setShowCustomersModal(open);
                if (!open) setCustomersSearchQuery(''); // Reset search on close
            }}>
                <DialogContent className="sm:max-w-lg max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {customersModalFilter === 'active' ? 'Clienți Activi' : 'Toți Clienții'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Search input */}
                    <div className="relative mt-2">
                        <input
                            type="text"
                            placeholder="Caută după număr sau nume..."
                            value={customersSearchQuery}
                            onChange={(e) => setCustomersSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 pl-10 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                            autoFocus
                        />
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    
                    <div className="space-y-3 mt-4 max-h-[55vh] overflow-y-auto">
                        {(customersModalFilter === 'active' 
                            ? sortCustomers(customers.filter(c => c.item_count > 0))
                            : sortCustomers(customers)
                        ).filter(customer => {
                            if (!customersSearchQuery.trim()) return true;
                            const query = customersSearchQuery.toLowerCase().trim();
                            const customerId = String(customer.customer_id || '').toLowerCase();
                            const customerName = String(customer.name || '').toLowerCase();
                            return customerId.includes(query) || customerName.includes(query);
                        }).map(customer => (
                            <div 
                                key={customer.customer_id}
                                className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100"
                            >
                                <div className="flex-1">
                                    <p className="font-semibold text-zinc-900">{customer.customer_id}</p>
                                    {customer.name && (
                                        <p className="text-sm text-zinc-500">{customer.name}</p>
                                    )}
                                    {customer.phone && (
                                        <p className="text-xs text-zinc-400">{customer.phone}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {customer.item_count > 0 && (
                                        <span className="text-xs bg-violet-100 text-violet-600 px-2 py-1 rounded-full">
                                            {customer.item_count} prod
                                        </span>
                                    )}
                                    <button
                                        onClick={() => {
                                            setEditCustomerData({
                                                customer_id: customer.customer_id,
                                                name: customer.name || '',
                                                phone: customer.phone || '',
                                                address: customer.address || ''
                                            });
                                            setShowEditCustomer(true);
                                        }}
                                        className="p-2 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded"
                                        title="Editează client"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleViewOrder(customer)}
                                        className="p-2 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                        title="Vezi comanda"
                                    >
                                        <Eye size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {customers.length === 0 && (
                            <p className="text-center text-zinc-500 py-8">Nu există clienți</p>
                        )}
                        {customers.length > 0 && customersSearchQuery && 
                         (customersModalFilter === 'active' 
                            ? customers.filter(c => c.item_count > 0)
                            : customers
                         ).filter(customer => {
                            const query = customersSearchQuery.toLowerCase().trim();
                            const customerId = String(customer.customer_id || '').toLowerCase();
                            const customerName = String(customer.name || '').toLowerCase();
                            return customerId.includes(query) || customerName.includes(query);
                         }).length === 0 && (
                            <p className="text-center text-zinc-500 py-8">Niciun client găsit pentru "{customersSearchQuery}"</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* FAB Button */}
            <button
                onClick={handleOpenScan}
                className="fixed bottom-6 right-6 w-14 h-14 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-all hover:scale-105"
                data-testid="fab-scan-button"
            >
                <ScanLine size={24} />
            </button>
            
            {/* Box Assignment Modal (when moving from orders to reservations) */}
            <Dialog open={showBoxAssignModal} onOpenChange={setShowBoxAssignModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Box size={20} />
                            Atribuie Cutie
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {boxAssignCustomer && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                                <p className="font-bold text-amber-800 text-lg">
                                    Client: {boxAssignCustomer}
                                </p>
                                <p className="text-sm text-amber-600 mt-1">
                                    Produsele au fost mutate în rezervări.
                                    <br/>În ce cutie le punem?
                                </p>
                            </div>
                        )}
                        
                        {freeBoxes.length > 0 && (
                            <div>
                                <Label className="text-xs text-zinc-500">Cutii libere:</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {freeBoxes.slice(0, 10).map(box => (
                                        <button
                                            key={box.id || `box-${box.box_number}`}
                                            type="button"
                                            onClick={() => setBoxNumber(box.box_number.toString())}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                boxNumber === box.box_number.toString()
                                                    ? 'bg-amber-500 text-white'
                                                    : 'bg-zinc-100 text-zinc-700 hover:bg-amber-100'
                                            }`}
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                    {/* F button for "fără cutie" */}
                                    <button
                                        type="button"
                                        onClick={() => setBoxNumber('F')}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                            boxNumber === 'F'
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                        }`}
                                        title="Fără cutie"
                                    >
                                        F
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <Label>Număr Cutie (sau F pentru fără)</Label>
                            <Input
                                value={boxNumber}
                                onChange={(e) => setBoxNumber(e.target.value.toUpperCase())}
                                placeholder="Ex: 1, 2, F..."
                                className="mt-1 text-center text-2xl font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAssignBoxFromOrders();
                                }}
                            />
                            <p className="text-xs text-zinc-500 mt-1 text-center">
                                "F" = Fără cutie fizică
                            </p>
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowBoxAssignModal(false);
                                    setBoxAssignCustomer(null);
                                }} 
                                className="flex-1"
                            >
                                Mai târziu
                            </Button>
                            <Button 
                                onClick={handleAssignBoxFromOrders} 
                                className="flex-1 bg-amber-500 hover:bg-amber-600"
                                disabled={!boxNumber}
                            >
                                <Box size={16} className="mr-2" />
                                Confirmă
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Location Box Modal (L-type boxes for unconfirmed orders) */}
            <Dialog open={showLocationBoxModal} onOpenChange={setShowLocationBoxModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-purple-600">
                            <Box size={20} />
                            Localizare Comandă
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {locationBoxCustomer && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                                <p className="font-bold text-purple-800 text-lg">
                                    Client: {locationBoxCustomer}
                                </p>
                                <p className="text-sm text-purple-600 mt-1">
                                    În ce cutie L este această comandă?
                                </p>
                            </div>
                        )}
                        
                        {/* Quick L buttons */}
                        <div>
                            <Label className="text-xs text-zinc-500">Cutii L (Large) pentru comenzi:</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                    <button
                                        key={`L${num}`}
                                        type="button"
                                        onClick={() => setLocationBox(`L${num}`)}
                                        className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                                            locationBox === `L${num}`
                                                ? 'bg-purple-500 text-white'
                                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        }`}
                                    >
                                        L{num}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Manual input */}
                        <div>
                            <Label className="text-xs text-zinc-500">Sau scrie manual:</Label>
                            <Input
                                value={locationBox}
                                onChange={(e) => setLocationBox(e.target.value.toUpperCase())}
                                placeholder="Ex: L1, L15..."
                                className="mt-1 text-center text-xl font-bold"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAssignLocationBox();
                                }}
                            />
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                            {locationBoxCustomer && customers.find(c => c.customer_id === locationBoxCustomer)?.location_box && (
                                <Button 
                                    variant="outline"
                                    onClick={() => {
                                        handleRemoveLocationBox(locationBoxCustomer);
                                        setShowLocationBoxModal(false);
                                    }}
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                    Șterge
                                </Button>
                            )}
                            <Button 
                                variant="outline" 
                                onClick={() => setShowLocationBoxModal(false)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleAssignLocationBox} 
                                className="flex-1 bg-purple-500 hover:bg-purple-600"
                                disabled={!locationBox}
                            >
                                <Box size={16} className="mr-2" />
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ComenziV2;
